import pluralize from 'pluralize';
import {
  FETCH_STATE_NONE,
  FETCH_STATE_PENDING,
  FETCH_STATE_SUCCESS,
  FETCH_STATE_ERROR,
} from '~/config/constants';

function getDefaultState() {
  return {
    collection: [],
    resource: {},
    collectionFetchState: FETCH_STATE_NONE,
    resourceFetchState: FETCH_STATE_NONE,
    page: 1,
    total_pages: 1,
    searchQuery: null,
    searchOrder: {
      column: 'name',
      direction: 'ascending',
    },
    apiValidation: {},
  };
}

export class RestHelper {
  constructor(endpoint, namespace) {
    this.endpoint = endpoint;
    this.namespace = namespace;
    this.resourceName = pluralize.singular(this.endpoint);
    this.collectionName = this.endpoint;
  }

  defaultState = () => getDefaultState()

  mutations = {
    getResourceStart(state) {
      state.resourceFetchState = FETCH_STATE_PENDING;
    },
    getResourceSuccess(state, action) {
      Object.assign(state, { resource: action.payload.data });
      state.resourceFetchState = FETCH_STATE_SUCCESS;
    },
    getResourceError(state) {
      state.resourceFetchState = FETCH_STATE_ERROR;
    },
    storeResourceSuccess(state) {
      state.resourceFetchState = FETCH_STATE_SUCCESS;
    },
    storeResourceError(state, action) {
      if (action.payload.response.data.error) {
        state.apiValidation = action.payload.response.data.error;
      }
      state.resourceFetchState = FETCH_STATE_ERROR;
    },
    deleteResourceError(state) {
      state.resourceFetchState = FETCH_STATE_ERROR;
    },
    deleteResourceSuccess(state) {
      state.resourceFetchState = FETCH_STATE_SUCCESS;
    },
    getCollectionStart(state) {
      state.collectionFetchState = FETCH_STATE_PENDING;
    },
    getCollectionSuccess(state, action) {
      Object.assign(state, action.payload.data);
      state.collectionFetchState = FETCH_STATE_SUCCESS;
    },
    getCollectionError(state) {
      state.collectionFetchState = FETCH_STATE_ERROR;
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
    setSearchOrder(state, attrs) {
      Object.assign(state.searchOrder, attrs);
    },
    setSearchQuery(state, value) {
      Object.assign(state, { searchQuery: value });
    },
    setPage(state, value) {
      Object.assign(state, { page: value });
    },
  };

  actions = {
    getResource: ({ commit, rootGetters }, { id }) => {
      commit(
        'api/fetch',
        {
          url: `/${this.endpoint}/${id}`,
          method: 'GET',
          headers: {
            ...rootGetters['auth/getHeaders'],
          },
          onStart: [`${this.namespace}/getResourceStart`],
          onSuccess: [`${this.namespace}/getResourceSuccess`],
          onError: [
            `${this.namespace}/getResourceError`,
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'getResourceError',
              params: { resourceName: this.resourceName },
            },
          ],
        },
        { root: true },
      );
    },
    newResource: ({ commit, rootGetters }) => {
      if (rootGetters['auth/isAuthorized']) {
        commit(
          'api/fetch',
          {
            url: `/${this.endpoint}/new`,
            method: 'GET',
            headers: {
              ...rootGetters['auth/getHeaders'],
            },
            onSuccess: [`${this.namespace}/getResourceSuccess`],
            onError: [`${this.namespace}/getResourceError`],
          },
          { root: true },
        );
      }
    },
    editResource: ({ commit, rootGetters }, { id }) => {
      commit(
        'api/fetch',
        {
          url: `/${this.endpoint}/${id}/edit`,
          method: 'GET',
          headers: {
            ...rootGetters['auth/getHeaders'],
          },
          onStart: [`${this.namespace}/getResourceStart`],
          onSuccess: [`${this.namespace}/getResourceSuccess`],
          onError: [
            `${this.namespace}/getResourceError`,
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'getResourceError',
              params: { resourceName: this.resourceName },
            },
          ],
        },
        { root: true },
      );
    },
    setOrder: ({ commit, dispatch, state }, attrs) => {
      commit('setSearchOrder', attrs);
      dispatch('getCollectionPage', state.page);
    },
    getCollectionPage: ({ dispatch, state }, page = 1) => {
      dispatch('getCollection', {
        query: state.searchQuery,
        page,
      });
    },
    getCollection: ({ commit, rootGetters, state }, { query, page }) => {
      commit('setSearchQuery', query);
      commit('setPage', page);

      const path = query ? '/search' : '/';

      commit(
        'api/fetch',
        {
          url: `/${this.endpoint}${path}`,
          method: 'GET',

          params: {
            fulltext: query,
            page,
            sort: `${state.searchOrder.column} ${state.searchOrder.direction}`,
          },

          headers: {
            ...rootGetters['auth/getHeaders'],
          },
          onStart: [`${this.namespace}/getCollectionStart`],
          onSuccess: [`${this.namespace}/getCollectionSuccess`],
          onError: [
            `${this.namespace}/getCollectionError`,
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'getCollectionError',
              params: { resourceName: this.resourceName },
            },
          ],
        },
        { root: true },
      );
    },
    storeResource: (
      { commit, rootGetters, state },
      { onSuccess = [] },
    ) => {
      state.apiValidation = {};
      return new Promise((resolve, reject) => {
        commit(
          'api/fetch',
          {
            url: state.resource.id ? `/${this.endpoint}/${state.resource.id}` : `/${this.endpoint}`,
            method: state.resource.id ? 'PUT' : 'POST',
            body: {
              [this.resourceName]: state.resource,
            },
            headers: rootGetters['auth/getHeaders'],
            onSuccess: [
              `${this.namespace}/storeResourceSuccess`,
              {
                type: 'flashMessages/addMessage',
                kind: 'success',
                key: 'storeResourceSuccess',
                params: {
                  resourceName: this.resourceName,
                  resource: state.resource,
                },
              },
              ...onSuccess,
            ],
            onError: [
              `${this.namespace}/storeResourceError`,
              {
                type: 'flashMessages/addMessage',
                kind: 'error',
                key: 'storeResourceError',
                withTimeout: true,
                params: { resourceName: this.resourceName },
              },
            ],
            promise: {
              resolve,
              reject,
            },

          },
          { root: true },
        );
      });
    },
    deleteResource: ({ commit, rootGetters, state }, id) => {
      commit(
        'api/fetch',
        {
          url: `/${this.endpoint}/${id}`,
          method: 'DELETE',
          headers: rootGetters['auth/getHeaders'],
          onSuccess: [
            `${this.namespace}/deleteResourceSuccess`,
            {
              type: 'flashMessages/addMessage',
              kind: 'success',
              key: 'deleteResourceSuccess',
              params: {
                resourceName: this.resourceName,
                resource: state.resource,
              },
            },
            {
              type: `${this.namespace}/getCollection`,
              query: state.searchQuery,
              page: state.page,
            },
          ],
          onError: [
            `${this.namespace}/deleteResourceError`,
            {
              type: 'flashMessages/addMessage',
              kind: 'error',
              key: 'deleteResourceError',
              params: { resourceName: this.resourceName },
            },
          ],
        },
        { root: true },
      );
    },

    storeResourceSuccess({ commit }) {
      commit('storeResourceSuccess');
    },
    storeResourceError({ commit }, data) {
      commit('storeResourceError', data);
    },
    deleteResourceError({ commit }) {
      commit('deleteResourceError');
    },
    deleteResourceSuccess({ commit }) {
      commit('deleteResourceSuccess');
    },
    getResourceStart({ commit }) {
      commit('getResourceStart');
    },
    getResourceSuccess({ commit }, payload) {
      commit('getResourceSuccess', payload);
    },
    getResourceError({ commit }, payload) {
      commit('getResourceError', payload);
    },
    getCollectionStart({ commit }) {
      commit('getCollectionStart');
    },
    getCollectionSuccess({ commit }, payload) {
      commit('getCollectionSuccess', payload);
    },
    getCollectionError({ commit }, payload) {
      commit('getCollectionError', payload);
    },
    resetState({ commit }) {
      commit('resetState');
    },
  };

  getters = {
    isSearching(state) {
      return state.searchQuery;
    },
    isCollectionLoaded(state) {
      return (
        state.collectionFetchState === FETCH_STATE_SUCCESS
        || state.collectionFetchState === FETCH_STATE_ERROR
      );
    },
    isResourceLoaded(state) {
      return (
        state.resourceFetchState === FETCH_STATE_SUCCESS
        || state.resourceFetchState === FETCH_STATE_ERROR
      );
    },
    isCollectionEmpty(state) {
      return state.collection.length === 0;
    },
  };
}
